import { useEffect, useState, useRef, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { LayoutDashboard, CheckSquare, Calendar, Brain, Rocket } from "lucide-react";
import { useBrainDump } from "@/components/BrainDumpProvider";

/**
 * BottomTabBar — phone-only bottom navigation (hidden from md+ where the
 * sidebar is visible). Five tabs covering the highest-frequency surfaces:
 *
 *   Today   →  /dashboard      (unreviewed-incident badge)
 *   Tasks   →  /tasks          (overdue-task badge)
 *   Calendar→  /calendar       (schedule-conflict badge)
 *   Sprints →  /sprints        (reserved for behind-pace badge)
 *   Brain   →  open Brain Dump (action, never a badge)
 *
 * Badges come from GET /api/nav/badges (single round-trip, polled every 60s
 * and re-fetched on tab focus / visibility change). Tiny red dot + numeric
 * count (or 9+ when >9). Capped so it never overflows the pill.
 */
const TABS = [
  { to: "/dashboard", label: "Today",    icon: LayoutDashboard, key: "today",    testid: "tab-today" },
  { to: "/tasks",     label: "Tasks",    icon: CheckSquare,     key: "tasks",    testid: "tab-tasks" },
  { to: "/calendar",  label: "Calendar", icon: Calendar,        key: "calendar", testid: "tab-calendar" },
  { to: "/sprints",   label: "Sprints",  icon: Rocket,          key: "sprints",  testid: "tab-sprints" },
];

/**
 * Small red-dot badge for the tab-bar icons. Shows the count (capped at
 * 9+) or a bare dot when count === 0 but a "reason" is set. Positioned
 * absolutely to the top-right of the icon so it doesn't shift layout.
 */
function TabBadge({ count, title }) {
  if (!count || count < 1) return null;
  const label = count > 9 ? "9+" : String(count);
  return (
    <span
      className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full
                 bg-red-500 text-white text-[10px] font-semibold leading-4
                 flex items-center justify-center ring-2 ring-zinc-950
                 pointer-events-none"
      title={title}
      data-testid="tab-badge"
    >
      {label}
    </span>
  );
}

export default function BottomTabBar() {
  const location = useLocation();
  const { open: openBrainDump } = useBrainDump();
  const [badges, setBadges] = useState({
    today: { count: 0, reason: null },
    tasks: { count: 0, reason: null },
    calendar: { count: 0, reason: null },
    sprints: { count: 0, reason: null },
  });
  const timerRef = useRef(null);

  const fetchBadges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nav/badges`, { withCredentials: true });
      setBadges(res.data);
    } catch (_) {
      /* silent — stale badges are fine */
    }
  }, []);

  // Initial fetch + 60s polling + refresh on focus/visibility
  useEffect(() => {
    fetchBadges();
    timerRef.current = setInterval(fetchBadges, 60_000);
    const onFocus = () => fetchBadges();
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchBadges();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchBadges]);

  // Also refetch when the route changes (user probably just acted on something)
  useEffect(() => {
    fetchBadges();
  }, [location.pathname, fetchBadges]);

  // Hide on the login / auth callback paths
  if (location.pathname === "/login" || location.pathname.startsWith("/auth")) {
    return null;
  }

  return (
    <nav
      aria-label="Primary"
      data-testid="bottom-tab-bar"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 no-print
                 border-t border-zinc-800 bg-zinc-950/95 backdrop-blur
                 supports-[backdrop-filter]:bg-zinc-950/80"
      style={{
        // Respect iPhone home-indicator safe area
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <ul className="flex items-stretch justify-between">
        {TABS.map(({ to, label, icon: Icon, key, testid }) => {
          const b = badges[key] || {};
          return (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex h-14 flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors
                   ${isActive ? "text-violet-300" : "text-zinc-400 hover:text-zinc-200"}`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative inline-flex">
                      <Icon
                        className={`w-5 h-5 ${isActive ? "stroke-[2.5]" : ""}`}
                        aria-hidden="true"
                      />
                      <TabBadge count={b.count} title={b.reason || label} />
                    </span>
                    <span>{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
        {/* Brain Dump — action, not a route. Visually the same shape so it
            feels native. Active style is permanent (it's an "open" affordance). */}
        <li className="flex-1">
          <button
            type="button"
            onClick={openBrainDump}
            data-testid="tab-brain-dump"
            className="flex w-full h-14 flex-col items-center justify-center gap-0.5
                       text-[10px] font-medium text-purple-300 hover:text-purple-200
                       transition-colors"
            aria-label="Open Brain Dump"
          >
            <Brain className="w-5 h-5" aria-hidden="true" />
            <span>Brain</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
